import { useEffect, useRef, useState } from "react";
import { getProjetosAcompanhamento, criarProjeto, excluirProjeto, inativarProjeto, atualizarStatusProjeto, atualizarImportancia, atualizarProjeto, vincularTicket, desvincularTicket, atualizarDescricaoTicket } from "../../api/projetos.api";

const REFRESH_SEC = 120;

function pctColor(v) {
  if (v === null || v === undefined) return "#475569";
  if (v >= 70) return "#4ade80";
  if (v >= 40) return "#fbbf24";
  return "#f87171";
}

function importanciaColor(v) {
  if (!v && v !== 0) return { text: "#475569", bg: "rgba(148,163,184,0.08)", border: "rgba(148,163,184,0.15)" };
  if (v >= 700) return { text: "#f87171", bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.25)" };
  if (v >= 300) return { text: "#fbbf24", bg: "rgba(251,191,36,0.1)",  border: "rgba(251,191,36,0.25)"  };
  return           { text: "#4ade80", bg: "rgba(74,222,128,0.1)",  border: "rgba(74,222,128,0.25)"  };
}

const NIVEL_COLORS = ["#60a5fa", "#a78bfa", "#22d3ee", "#fbbf24", "#4ade80"];

const GP_STATUS = {
  1:  { label: "Em dia",                       color: "#4ade80"  },
  2:  { label: "Não iniciado",                 color: "#94a3b8"  },
  3:  { label: "Parado",                       color: "#f87171"  },
  4:  { label: "Em Atraso",                    color: "#f87171"  },
  5:  { label: "Parado Cliente",               color: "#fb923c"  },
  6:  { label: "Parado Lexsis Produção",       color: "#fb923c"  },
  7:  { label: "Parado Lexsis Implantação",    color: "#fb923c"  },
  8:  { label: "Parado Lexsis Comercial",      color: "#fb923c"  },
  9:  { label: "Não Iniciado Com DeadLine",    color: "#fbbf24"  },
  10: { label: "Não Iniciado Sem DeadLine",    color: "#94a3b8"  },
  12: { label: "Aguardando Feedback",          color: "#a78bfa"  },
  13: { label: "Em Produção - Acompanhamento", color: "#22d3ee"  },
  14: { label: "Em andamento",                 color: "#60a5fa"  },
  15: { label: "Fila homologação",             color: "#fbbf24"  },
  16: { label: "Finalizado",                   color: "#4ade80"  },
  17: { label: "Restrição",                    color: "#f87171"  },
  18: { label: "Em Piloto",                    color: "#22d3ee"  },
  19: { label: "Em Testes",                    color: "#a78bfa"  },
  20: { label: "Em desenvolvimento",           color: "#60a5fa"  },
};
const gpStatus = (id) => GP_STATUS[id] ?? { label: "—", color: "#334155" };

/* Status que indicam entrega — progresso tratado como 100% */
/* Status de issues considerados fechados para fins de progresso */
const TICKET_FECHADOS = new Set([
  "Fechado", "Resolvido", "Em Teste", "Aguardando Testes",
  "Aguardando Versão", "Ajuste Especificação", "Feedback",
]);

const isTicketFechado = (t) =>
  t.ticket_fechado ||
  (t.ticket_status && TICKET_FECHADOS.has(t.ticket_status));

/* Cor por status do ticket */
const STATUS_TICKET_COLOR = {
  "Novo":                       "#a78bfa",
  "Em curso":                   "#fbbf24",
  "Aguardando Desenvolvimento": "#38bdf8",
  "Parado":                     "#fb923c",
  "Resolvido":                  "#4ade80",
  "Fechado":                    "#4ade80",
  "Em Teste":                   "#4ade80",
  "Aguardando Testes":          "#4ade80",
  "Feedback":                   "#4ade80",
  "Aguardando Versão":          "#4ade80",
  "Ajuste Especificação":       "#e879f9",
};
const ticketStatusColor = (s) => STATUS_TICKET_COLOR[s] ?? "#94a3b8";

const FINISHED_STATUS_IDS = new Set([12, 15, 16, 18, 19]);

function calcPct(proj) {
  if (FINISHED_STATUS_IDS.has(proj.id_gp_status)) return 100;
  if (proj.children && proj.children.length > 0) {
    const vals = proj.children.map(c => calcPct(c));
    return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
  }
  if (proj.tickets && proj.tickets.length > 0)
    return Math.round(proj.tickets.filter(t => isTicketFechado(t)).length / proj.tickets.length * 100);
  return proj.percentual_concluido ?? 0;
}

const STATUS_OPCOES = [
  { id: 1,  label: "Em dia"       },
  { id: 2,  label: "Não iniciado" },
  { id: 3,  label: "Parado"       },
  { id: 4,  label: "Em Atraso"    },
  { id: 16, label: "Finalizado"   },
  { id: 17, label: "Restrição"    },
  { id: 18, label: "Em Piloto"    },
];
const nc = (depth) => NIVEL_COLORS[Math.min(depth, NIVEL_COLORS.length - 1)];

function groupByProjeto(rows) {
  const map = new Map();
  for (const row of rows) {
    const k = row.id_projeto;
    if (!map.has(k)) {
      map.set(k, {
        id_projeto: row.id_projeto, id_projeto_pai: row.id_projeto_pai,
        descricao: row.descricao, nivel: row.nivel, cliente: row.cliente,
        importancia: row.importancia, deadline: row.deadline,
        percentual_concluido: row.percentual_concluido,
        id_gp_status: row.id_gp_status,
        tickets: [], children: [],
      });
    }
    const ticketId = row.ticketid || row.ticketId || row.id_ticket_prog;
    if (ticketId) {
      map.get(k).tickets.push({
        id: ticketId, subject: row.subject, sprint: row.sprint,
        desenvolvedor: row.desenvolvedor, grupo_entrega: row.grupo_entrega,
        programado: row.programado, sistema: row.sistema,
        responsavel: row.responsavel, size: row.size, bloqueado: row.bloqueado,
        done_ratio: row.done_ratio, ticket_fechado: row.ticket_fechado,
        ticket_status: row.ticket_status || null,
      });
    }
  }
  return map;
}

function buildTree(rows) {
  const map = groupByProjeto(rows);
  const roots = [];
  for (const p of map.values()) {
    if (p.id_projeto_pai && map.has(p.id_projeto_pai)) map.get(p.id_projeto_pai).children.push(p);
    else roots.push(p);
  }
  const sort = (arr) => { arr.sort((a, b) => (b.importancia || 0) - (a.importancia || 0)); arr.forEach(p => sort(p.children)); };
  sort(roots);
  return roots;
}


function updateProjetoInTree(nodes, id, updates) {
  return nodes.map(n => ({
    ...n,
    ...(n.id_projeto === id ? updates : {}),
    children: updateProjetoInTree(n.children, id, updates),
  }));
}

function flatTree(nodes, depth = 0, result = []) {
  for (const n of nodes) { result.push({ ...n, _depth: depth }); flatTree(n.children, depth + 1, result); }
  return result;
}

/* ══════════════════════════════════════════════════
   Page
══════════════════════════════════════════════════ */
export default function Acompanhamento({ tipo = 1, titulo = "Acompanhamento" }) {
  const [projetos,    setProjetos]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(false);
  const [selected,    setSelected]    = useState(null);
  const [countdown,   setCountdown]   = useState(REFRESH_SEC);
  const [criarModal,    setCriarModal]    = useState(null);
  const [confirmAcao,   setConfirmAcao]   = useState(null);
  const [vincularModal, setVincularModal] = useState(null); // { proj }
  const loadRef = useRef(null);
  const isFirst = useRef(true);

  async function load(bust = false) {
    const first = isFirst.current;
    if (first) setLoading(true);
    setError(false);
    try {
      const raw  = await getProjetosAcompanhamento(bust, tipo);
      const data = (Array.isArray(raw) ? raw : []).filter(r => r.tipo == null || r.tipo === tipo);
      const tree = buildTree(data);
      setProjetos(tree);
      isFirst.current = false;
    } catch { if (first) setError(true); }
    finally  { if (first) setLoading(false); }
  }

  loadRef.current = load;

  function handleRefresh() { loadRef.current(true); setCountdown(REFRESH_SEC); }

  useEffect(() => {
    loadRef.current();
    setCountdown(REFRESH_SEC);
    const id = setInterval(() => {
      setCountdown(prev => { if (prev <= 1) { loadRef.current(); return REFRESH_SEC; } return prev - 1; });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  async function handleCriar(dados) {
    await criarProjeto({ ...dados, tipo });
    setCriarModal(null);
    handleRefresh();
  }
  async function handleExcluir(proj) {
    await excluirProjeto(proj.id_projeto);
    setConfirmAcao(null);
    if (selected?.id_projeto === proj.id_projeto) setSelected(null);
    handleRefresh();
  }
  async function handleInativar(proj) {
    await inativarProjeto(proj.id_projeto);
    setConfirmAcao(null);
    if (selected?.id_projeto === proj.id_projeto) setSelected(null);
    handleRefresh();
  }

  if (loading) return <Skeleton />;
  if (error) return (
    <div style={errorWrap}>
      <span style={{ fontSize: 32, color: "#fbbf24" }}>⚠</span>
      <p style={{ color: "#94a3b8" }}>Erro ao carregar projetos</p>
      <button onClick={handleRefresh} style={btnSec}>Tentar novamente</button>
    </div>
  );

  const flat = flatTree(projetos);

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h1 style={h1}>{titulo}</h1>
          <span style={badge}>{flat.filter(p => (p._depth > 0 ? p._depth : Math.max(0, (p.nivel||1)-1)) === 0).length} projetos</span>
          <span style={{ ...badge, background: "rgba(167,139,250,0.12)", borderColor: "rgba(167,139,250,0.25)", color: "#a78bfa" }}>
            {flat.filter(p => (p._depth > 0 ? p._depth : Math.max(0, (p.nivel||1)-1)) > 0).length} subprojetos
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={handleRefresh} title="Atualizar agora" style={refreshBtn}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(96,165,250,0.18)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(96,165,250,0.08)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.2)"; }}
          >↻</button>
          <CountdownRing seconds={countdown} total={REFRESH_SEC} />
        </div>
      </header>

      {/* ── Split layout ── */}
      <div style={splitWrap}>

        {/* ── Painel esquerdo: árvore ── */}
        <div style={leftPanel}>
          <div style={treeHeader}>
            <span style={treeTitleStyle}>Projetos</span>
            <button
              style={newRootBtn}
              onClick={() => setCriarModal({ pai: null })}
              title="Novo projeto raiz"
            >＋ Novo</button>
          </div>
          <div style={treeList}>
            {flat.map(proj => {
              const depth    = proj._depth;
              const vDepth   = depth > 0 ? depth : Math.max(0, (proj.nivel || 1) - 1);
              const color    = nc(vDepth);
              const isSel    = selected?.id_projeto === proj.id_projeto;
              const iColor   = importanciaColor(proj.importancia);
              const pct      = calcPct(proj);
              const hasChild = proj.children.length > 0;
              const isRoot   = vDepth === 0;

              return (
                <div
                  key={proj.id_projeto}
                  onClick={() => setSelected(isSel ? null : proj)}
                  style={{
                    ...treeItem,
                    marginLeft: vDepth * 20,
                    borderLeftColor: vDepth > 0 ? color : isRoot ? `${color}60` : "transparent",
                    borderLeftWidth: vDepth > 0 ? 3 : isRoot ? 3 : 0,
                    background: isSel
                      ? `linear-gradient(90deg, ${color}18, ${color}08)`
                      : isRoot ? `${color}06` : "transparent",
                    boxShadow: isSel ? `inset 0 0 0 1px ${color}30` : "none",
                    paddingTop:    isRoot ? 9 : 7,
                    paddingBottom: isRoot ? 9 : 7,
                  }}
                >
                  {/* Conector L para filhos */}
                  {vDepth > 0 && <span style={{ color, fontSize: 10, marginRight: 2, opacity: 0.6 }}>└</span>}

                  <div style={treeItemLeft}>
                    <span style={{ color, fontSize: isRoot ? 11 : 10, fontWeight: 800, whiteSpace: "nowrap" }}>#{proj.id_projeto}</span>
                    <span style={{
                      color: isSel ? "#f1f5f9" : isRoot ? "#e2e8f0" : "#94a3b8",
                      fontSize: isRoot ? 13 : 12,
                      fontWeight: isRoot ? 700 : isSel ? 700 : 500,
                      flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {proj.descricao}
                    </span>
                    {hasChild && (
                      <span style={subBadge}>{proj.children.length}</span>
                    )}
                  </div>

                  <div style={treeItemRight}>
                    {proj.id_gp_status != null && (() => {
                      const st = gpStatus(proj.id_gp_status);
                      return (
                        <span style={{ ...statusPill, color: st.color, borderColor: `${st.color}35`, background: `${st.color}12` }}>
                          {st.label}
                        </span>
                      );
                    })()}
                    {proj.importancia != null && (
                      <span style={{ ...impPill, color: iColor.text, background: iColor.bg, borderColor: iColor.border }}>
                        {proj.importancia}
                      </span>
                    )}
                    <span style={{ color: pctColor(pct), fontSize: 10, fontWeight: 700, minWidth: 28, textAlign: "right" }}>
                      {pct}%
                    </span>
                    {/* Ações inline */}
                    <div style={treeActions} onClick={e => e.stopPropagation()}>
                      <button title="Adicionar sub-projeto" style={{ ...treeBtn, color: "#4ade80" }}
                        onClick={() => setCriarModal({ pai: proj })}>＋</button>
                      <button title="Inativar" style={{ ...treeBtn, color: "#fbbf24" }}
                        onClick={() => setConfirmAcao({ tipo: "inativar", proj })}>⏸</button>
                      <button title="Excluir" style={{ ...treeBtn, color: "#f87171" }}
                        onClick={() => setConfirmAcao({ tipo: "excluir", proj })}>✕</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Painel direito: detalhes ── */}
        <div style={rightPanel}>
          {!selected ? (
            <div style={emptyDetail}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>◈</div>
              <p style={{ color: "#475569", fontSize: 13 }}>Selecione um projeto para ver os detalhes</p>
            </div>
          ) : (
            <ProjetoDetail
              proj={selected}
              onCriarFilho={(pai) => setCriarModal({ pai })}
              onExcluir={(p) => setConfirmAcao({ tipo: "excluir", proj: p })}
              onInativar={(p) => setConfirmAcao({ tipo: "inativar", proj: p })}
              onVincularTicket={() => setVincularModal({ proj: selected })}
              onImportanciaChange={async (valor) => {
                const anterior = selected.importancia;
                const upd = { importancia: valor };
                setSelected(prev => ({ ...prev, ...upd }));
                setProjetos(prev => updateProjetoInTree(prev, selected.id_projeto, upd));
                try {
                  await atualizarImportancia(selected.id_projeto, valor);
                } catch {
                  setSelected(prev => ({ ...prev, importancia: anterior }));
                  setProjetos(prev => updateProjetoInTree(prev, selected.id_projeto, { importancia: anterior }));
                }
              }}
              onEditar={async (campos) => {
                await atualizarProjeto(selected.id_projeto, campos);
                handleRefresh();
              }}
              onDesvincularTicket={async (idTicket) => {
                await desvincularTicket(selected.id_projeto, idTicket);
                handleRefresh();
              }}
              onEditarDescricao={async (idTicket, descricao) => {
                await atualizarDescricaoTicket(selected.id_projeto, idTicket, descricao);
                handleRefresh();
              }}
              onStatusChange={async (idStatus) => {
                const anterior = selected.id_gp_status;
                const upd = { id_gp_status: idStatus };
                /* update otimista nos dois estados → painel esq + dir atualizam imediatamente */
                setSelected(prev => ({ ...prev, ...upd }));
                setProjetos(prev => updateProjetoInTree(prev, selected.id_projeto, upd));
                try {
                  await atualizarStatusProjeto(selected.id_projeto, idStatus);
                  handleRefresh();
                } catch {
                  setSelected(prev => ({ ...prev, id_gp_status: anterior }));
                  setProjetos(prev => updateProjetoInTree(prev, selected.id_projeto, { id_gp_status: anterior }));
                }
              }}
            />
          )}
        </div>
      </div>

      {criarModal && (
        <CriarProjetoModal
          pai={criarModal.pai}
          onClose={() => setCriarModal(null)}
          onConfirm={handleCriar}
        />
      )}
      {confirmAcao && (
        <ConfirmModal
          tipo={confirmAcao.tipo}
          proj={confirmAcao.proj}
          onClose={() => setConfirmAcao(null)}
          onConfirm={() => confirmAcao.tipo === "excluir"
            ? handleExcluir(confirmAcao.proj)
            : handleInativar(confirmAcao.proj)
          }
        />
      )}

      {vincularModal && (
        <VincularTicketModal
          proj={vincularModal.proj}
          onClose={() => setVincularModal(null)}
          onConfirm={async (idTicket, descricaoCustomizada) => {
            await vincularTicket(vincularModal.proj.id_projeto, idTicket, descricaoCustomizada);
            setVincularModal(null);
            /* bust=true garante que o backend não serve cache antigo */
            loadRef.current(true);
            setCountdown(REFRESH_SEC);
          }}
        />
      )}
    </div>
  );
}

/* ── ProjetoDetail ───────────────────────────────── */
function ProjetoDetail({ proj, onStatusChange, onVincularTicket, onDesvincularTicket, onImportanciaChange, onEditarDescricao, onEditar }) {
  const [editDescModal,  setEditDescModal]  = useState(null);
  const [editProjModal,  setEditProjModal]  = useState(false);
  const fechados = proj.tickets.filter(t => isTicketFechado(t)).length;
  const abertos  = proj.tickets.length - fechados;
  const pct    = calcPct(proj);
  const pColor = pctColor(pct);

  return (
    <div style={detailWrap}>
      {/* ── Cabeçalho ── */}
      <div style={detailHead}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
            <span style={{ color: nc(Math.max(0, (proj.nivel || 1) - 1)), fontSize: 12, fontWeight: 800 }}>#{proj.id_projeto}</span>
            <span style={nivelPill}>Nível {proj.nivel || 1}</span>
            <StatusSelect
              value={proj.id_gp_status}
              onChange={onStatusChange}
            />
            {proj.cliente && <span style={clientePill}>{proj.cliente}</span>}
            {proj.children.length > 0 && (
              <span style={{ ...nivelPill, color: "#60a5fa", borderColor: "rgba(96,165,250,0.2)", background: "rgba(96,165,250,0.08)" }}>
                {proj.children.length} sub-projeto{proj.children.length > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <h2 style={{ ...detailTitle, margin: 0 }}>{proj.descricao}</h2>
            <button
              title="Editar projeto"
              onClick={() => setEditProjModal(true)}
              style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", fontSize: 13, padding: 2, transition: "color 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.color = "#60a5fa"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "#475569"; }}
            >✎</button>
          </div>
          {proj.deadline && <div style={{ color: "#fbbf24", fontSize: 12, marginTop: 4 }}>📅 Deadline: {proj.deadline}</div>}
        </div>
        {proj.importancia != null && (
          <ImportanciaEditor
            value={proj.importancia}
            onChange={onImportanciaChange}
          />
        )}
      </div>

      {/* ── KPIs ── */}
      {proj.tickets.length > 0 && (
        <div style={kpiRow}>
          <KpiMini label="Total"    value={proj.tickets.length} color="#60a5fa" />
          <KpiMini label="Abertos"  value={abertos}             color="#fbbf24" />
          <KpiMini label="Fechados" value={fechados}            color="#4ade80" />
          <KpiMini label="Progresso" value={`${pct}%`} color={pColor}
            extra={<div style={{ height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden", marginTop: 4 }}>
              <div style={{ height: "100%", width: `${pct}%`, background: pColor, borderRadius: 3, transition: "width 0.5s" }} />
            </div>}
          />
        </div>
      )}

      {/* ── Sub-projetos ── */}
      {proj.children.length > 0 && (
        <div style={section}>
          <div style={sectionTitle}>Sub-projetos</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
            {proj.children.map(child => {
              const cpct   = calcPct(child);
              const cColor = nc(Math.max(0, (child.nivel || 2) - 1));
              return (
                <div key={child.id_projeto} style={{ ...glass, padding: "12px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ color: cColor, fontSize: 11, fontWeight: 800 }}>#{child.id_projeto}</span>
                    <span style={{ color: pctColor(cpct), fontSize: 11, fontWeight: 800 }}>{cpct}%</span>
                  </div>
                  <div style={{ color: "#e2e8f0", fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{child.descricao}</div>
                  <div style={{ height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${cpct}%`, background: pctColor(cpct), borderRadius: 3 }} />
                  </div>
                  <div style={{ color: "#334155", fontSize: 10, marginTop: 6 }}>{child.tickets.length} tickets</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Tickets ── */}
      {proj.children.length === 0 && (
        <div style={section}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={sectionTitle}>Tickets ({proj.tickets.length})</div>
            <button
              onClick={onVincularTicket}
              style={{ padding: "3px 12px", borderRadius: 7, border: "1px solid rgba(74,222,128,0.3)", background: "rgba(74,222,128,0.08)", color: "#4ade80", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(74,222,128,0.16)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(74,222,128,0.08)"; }}
            >
              ＋ Vincular ticket
            </button>
          </div>
          {proj.tickets.length === 0 ? (
            <div style={{ color: "#334155", fontSize: 12, padding: "16px 0" }}>Nenhum ticket vinculado</div>
          ) : (() => {
            const STATUS_ORDER = [
              "Novo", "Aguardando Desenvolvimento", "Em curso", "Parado",
              "Resolvido", "Em Teste", "Aguardando Testes", "Feedback",
              "Aguardando Versão", "Ajuste Especificação", "Fechado",
            ];
            const byStatus = {};
            for (const t of proj.tickets) {
              const key = t.ticket_status || "Sem status";
              if (!byStatus[key]) byStatus[key] = [];
              byStatus[key].push(t);
            }
            const groups = Object.keys(byStatus).sort((a, b) => {
              const ia = STATUS_ORDER.indexOf(a), ib = STATUS_ORDER.indexOf(b);
              if (ia === -1 && ib === -1) return a.localeCompare(b);
              if (ia === -1) return 1;
              if (ib === -1) return -1;
              return ia - ib;
            });
            return (
              <div style={{ overflowX: "auto" }}>
                <table style={table}>
                  <thead>
                    <tr>
                      {["ID", "Título", "Desenvolvedor", "Sprint", "Sistema", "Prog.", ""].map(c => (
                        <th key={c} style={th}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map(status => (
                      <>
                        <tr key={`grp-${status}`}>
                          <td colSpan={7} style={{ padding: "10px 12px 4px", borderBottom: "none" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{
                                display: "inline-block",
                                width: 8, height: 8, borderRadius: "50%",
                                background: ticketStatusColor(status), flexShrink: 0,
                              }} />
                              <span style={{ color: ticketStatusColor(status), fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8 }}>
                                {status}
                              </span>
                              <span style={{ color: "#334155", fontSize: 10, fontWeight: 700 }}>
                                ({byStatus[status].length})
                              </span>
                            </div>
                          </td>
                        </tr>
                        {byStatus[status].map(t => (
                          <tr key={t.id} style={{ cursor: "pointer" }}
                            onDoubleClick={() => window.open(`https://redmine.lexsis.com.br/issues/${t.id}`, "_blank")}
                            title="Duplo clique para abrir no Redmine"
                            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                          >
                            <td style={{ ...td, color: "#60a5fa", fontWeight: 700, whiteSpace: "nowrap", paddingLeft: 24 }}>#{t.id}</td>
                            <td style={{ ...td, maxWidth: 260, color: "#e2e8f0" }}>
                              <div>{t.subject || <span style={{ color: "#334155", fontStyle: "italic" }}>—</span>}</div>
                              {t.descricao_customizada && (
                                <div style={{ fontSize: 10, color: "#22d3ee", marginTop: 2 }}>
                                  ✎ {t.descricao_customizada}
                                </div>
                              )}
                            </td>
                            <td style={{ ...td, whiteSpace: "nowrap", color: "#94a3b8" }}>{t.desenvolvedor || "—"}</td>
                            <td style={{ ...td, whiteSpace: "nowrap", color: "#94a3b8" }}>{t.sprint || "—"}</td>
                            <td style={{ ...td, whiteSpace: "nowrap", color: "#94a3b8" }}>{t.sistema || "—"}</td>
                            <td style={{ ...td, textAlign: "center" }}>
                              {t.programado === "1" || t.programado === 1
                                ? <span style={chipSim}>Sim</span> : <span style={chipNao}>Não</span>}
                            </td>
                            <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }} onClick={e => e.stopPropagation()}>
                              <button
                                title="Editar descrição resumida"
                                onClick={() => setEditDescModal({ id: t.id, descricao_customizada: t.descricao_customizada || "" })}
                                style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", fontSize: 12, lineHeight: 1, padding: "2px 4px", transition: "color 0.15s" }}
                                onMouseEnter={e => { e.currentTarget.style.color = "#22d3ee"; }}
                                onMouseLeave={e => { e.currentTarget.style.color = "#475569"; }}
                              >✎</button>
                              <button
                                title="Desvincular ticket"
                                onClick={() => onDesvincularTicket(t.id)}
                                style={{ background: "none", border: "none", color: "#334155", cursor: "pointer", fontSize: 13, lineHeight: 1, padding: "2px 4px", transition: "color 0.15s" }}
                                onMouseEnter={e => { e.currentTarget.style.color = "#f87171"; }}
                                onMouseLeave={e => { e.currentTarget.style.color = "#334155"; }}
                              >✕</button>
                            </td>
                          </tr>
                        ))}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {editDescModal && (
        <EditarDescricaoModal
          ticket={editDescModal}
          onClose={() => setEditDescModal(null)}
          onConfirm={async (idTicket, descricao) => {
            await onEditarDescricao(idTicket, descricao);
            setEditDescModal(null);
          }}
        />
      )}

      {editProjModal && (
        <EditarProjetoModal
          proj={proj}
          onClose={() => setEditProjModal(false)}
          onConfirm={async (campos) => {
            await onEditar(campos);
            setEditProjModal(false);
          }}
        />
      )}
    </div>
  );
}

function KpiMini({ label, value, color, extra }) {
  return (
    <div style={{ ...glass, padding: "12px 16px", flex: 1, minWidth: 100 }}>
      <div style={{ color: "#475569", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
      <div style={{ color, fontSize: 22, fontWeight: 800, letterSpacing: -0.5, marginTop: 2 }}>{value}</div>
      {extra}
    </div>
  );
}

/* ── ImportanciaEditor ───────────────────────────── */
function ImportanciaEditor({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState("");
  const iColor = importanciaColor(value);

  function startEdit() {
    setDraft(value ?? "");
    setEditing(true);
  }

  function cancel() { setEditing(false); }

  async function save() {
    const num = parseInt(draft, 10);
    if (!isNaN(num) && num !== value) await onChange(num);
    setEditing(false);
  }

  function onKeyDown(e) {
    if (e.key === "Enter")  { e.preventDefault(); save(); }
    if (e.key === "Escape") cancel();
  }

  if (editing) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          autoFocus
          type="number"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={save}
          style={{
            width: 90, padding: "4px 10px", borderRadius: 8,
            border: `1px solid ${iColor.border}`,
            background: iColor.bg, color: iColor.text,
            fontSize: 13, fontWeight: 700, outline: "none",
          }}
        />
        <span style={{ color: "#334155", fontSize: 10 }}>Enter salva · Esc cancela</span>
      </div>
    );
  }

  return (
    <button
      onClick={startEdit}
      title="Clique para editar a importância"
      style={{
        ...impBadgeLg,
        color: iColor.text, background: iColor.bg, borderColor: iColor.border,
        cursor: "pointer", transition: "opacity 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
      onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
    >
      Importância {value ?? "—"} ✎
    </button>
  );
}

/* ── EditarProjetoModal ──────────────────────────── */
function EditarProjetoModal({ proj, onClose, onConfirm }) {
  const [form,   setForm]   = useState({
    descricao:   proj.descricao   || "",
    cliente:     proj.cliente     || "",
    deadline:    proj.deadline    ? proj.deadline.slice(0, 10) : "",
    importancia: proj.importancia != null ? String(proj.importancia) : "",
  });
  const [saving, setSaving] = useState(false);
  const [erro,   setErro]   = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.descricao.trim()) { setErro("Nome é obrigatório."); return; }
    setSaving(true); setErro(null);
    try {
      const campos = { descricao: form.descricao.trim() };
      if (form.cliente.trim())   campos.cliente     = form.cliente.trim();
      if (form.deadline)         campos.deadline    = form.deadline;
      if (form.importancia !== "") campos.importancia = Number(form.importancia);
      await onConfirm(campos);
    } catch (err) {
      setErro(err?.response?.data?.error || "Erro ao salvar.");
      setSaving(false);
    }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modalBox, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 800, margin: 0 }}>Editar Projeto</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#475569", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ color: "#475569", fontSize: 12 }}>
          #{proj.id_projeto} · Nível {proj.nivel || 1}
        </div>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <label style={fieldLabel}>
            Nome *
            <input
              autoFocus
              value={form.descricao}
              onChange={e => set("descricao", e.target.value)}
              style={fieldInput}
            />
          </label>
          <label style={fieldLabel}>
            Cliente
            <input
              value={form.cliente}
              onChange={e => set("cliente", e.target.value)}
              placeholder="Nome do cliente"
              style={fieldInput}
            />
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={fieldLabel}>
              Deadline
              <input
                type="date"
                value={form.deadline}
                onChange={e => set("deadline", e.target.value)}
                style={{ ...fieldInput, colorScheme: "dark" }}
              />
            </label>
            <label style={fieldLabel}>
              Importância
              <input
                type="number"
                value={form.importancia}
                onChange={e => set("importancia", e.target.value)}
                placeholder="ex: 500"
                min="0"
                style={fieldInput}
              />
            </label>
          </div>
          {erro && <div style={erroMsg}>{erro}</div>}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={btnSec} disabled={saving}>Cancelar</button>
            <button type="submit" style={{ ...btnPri, opacity: saving ? 0.7 : 1 }} disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── EditarDescricaoModal ────────────────────────── */
function EditarDescricaoModal({ ticket, onClose, onConfirm }) {
  const [descricao, setDescricao] = useState(ticket.descricao_customizada || "");
  const [saving,    setSaving]    = useState(false);
  const [erro,      setErro]      = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setErro(null);
    try {
      await onConfirm(ticket.id, descricao.trim() || null);
      onClose();
    } catch (err) {
      setErro(err?.response?.data?.error || "Erro ao salvar.");
      setSaving(false);
    }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modalBox, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 800, margin: 0 }}>Descrição Resumida</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#475569", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ color: "#475569", fontSize: 12 }}>
          Ticket: <span style={{ color: "#60a5fa", fontWeight: 700 }}>#{ticket.id}</span>
        </div>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <label style={fieldLabel}>
            Descrição resumida
            <input
              autoFocus
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              placeholder="ex: Valler DT Inicial e Final erro na impressão"
              style={fieldInput}
            />
            <span style={{ fontSize: 10, color: "#334155", marginTop: 3, display: "block" }}>
              Deixe em branco para usar o título original do Redmine
            </span>
          </label>
          {erro && <div style={erroMsg}>{erro}</div>}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={btnSec} disabled={saving}>Cancelar</button>
            <button type="submit" style={{ ...btnPri, opacity: saving ? 0.7 : 1 }} disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── VincularTicketModal ─────────────────────────── */
function VincularTicketModal({ proj, onClose, onConfirm }) {
  const [idTicket,  setIdTicket]  = useState("");
  const [descricao, setDescricao] = useState("");
  const [saving,    setSaving]    = useState(false);
  const [erro,      setErro]      = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    const num = parseInt(idTicket.replace(/\D/g, ""), 10);
    if (!num) { setErro("Informe um número de ticket válido."); return; }
    setSaving(true); setErro(null);
    try { await onConfirm(num, descricao.trim() || null); }
    catch (err) {
      setErro(err?.response?.data?.error || "Erro ao vincular ticket.");
      setSaving(false);
    }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modalBox, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 800, margin: 0 }}>Vincular Ticket</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#475569", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ color: "#475569", fontSize: 12 }}>
          Projeto: <span style={{ color: "#60a5fa", fontWeight: 700 }}>#{proj.id_projeto} — {proj.descricao}</span>
        </div>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <label style={fieldLabel}>
            Número do ticket *
            <input
              autoFocus
              value={idTicket}
              onChange={e => setIdTicket(e.target.value)}
              placeholder="ex: 217139"
              style={fieldInput}
            />
          </label>
          <label style={fieldLabel}>
            Descrição resumida
            <input
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              placeholder="ex: Valler DT Inicial e Final erro na impressão"
              style={fieldInput}
            />
            <span style={{ fontSize: 10, color: "#334155", marginTop: 3, display: "block" }}>
              Opcional — substitui o título do ticket no Acompanhamento de Tarefas
            </span>
          </label>
          {erro && <div style={erroMsg}>{erro}</div>}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={btnSec} disabled={saving}>Cancelar</button>
            <button type="submit" style={{ ...btnPri, opacity: saving ? 0.7 : 1 }} disabled={saving}>
              {saving ? "Vinculando..." : "Vincular"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── StatusSelect ────────────────────────────────── */
function StatusSelect({ value, onChange }) {
  const [saving, setSaving] = useState(false);
  const current = gpStatus(value);

  function handleChange(e) {
    const novoId = Number(e.target.value);
    if (novoId === value) return;
    setSaving(true);
    onChange(novoId).finally(() => setSaving(false));
  }

  return (
    <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <select
        value={value ?? ""}
        onChange={handleChange}
        disabled={saving}
        style={{
          appearance: "none",
          WebkitAppearance: "none",
          padding: "3px 24px 3px 10px",
          borderRadius: 8,
          border: `1px solid ${current.color}40`,
          background: `${current.color}14`,
          color: current.color,
          fontSize: 11,
          fontWeight: 700,
          cursor: saving ? "wait" : "pointer",
          outline: "none",
          transition: "all 0.15s",
        }}
      >
        {!value && <option value="">— sem status —</option>}
        {STATUS_OPCOES.map(s => (
          <option key={s.id} value={s.id} style={{ background: "#0b1629", color: gpStatus(s.id).color }}>
            {s.label}
          </option>
        ))}
      </select>
      <span style={{ position: "absolute", right: 7, pointerEvents: "none", color: current.color, fontSize: 9 }}>▾</span>
    </div>
  );
}

/* ── Modais ──────────────────────────────────────── */
function CriarProjetoModal({ pai, onClose, onConfirm }) {
  const [descricao,   setDescricao]   = useState("");
  const [importancia, setImportancia] = useState("");
  const [deadline,    setDeadline]    = useState("");
  const [saving,      setSaving]      = useState(false);
  const [erro,        setErro]        = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!descricao.trim()) { setErro("Descrição é obrigatória."); return; }
    setSaving(true); setErro(null);
    try {
      await onConfirm({
        descricao: descricao.trim(),
        id_projeto_pai: pai ? pai.id_projeto : null,
        nivel:          pai ? (pai.nivel || 1) + 1 : 1,
        importancia:    importancia ? Number(importancia) : null,
        deadline:       deadline || null,
        ativo:          1,
      });
    } catch (err) {
      setErro(err?.response?.data?.error || "Erro ao criar projeto.");
      setSaving(false);
    }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>
        <div style={modalHead}>
          <h2 style={modalTitle}>{pai ? "Novo Sub-projeto" : "Novo Projeto"}</h2>
          <button onClick={onClose} style={closeBtn}>✕</button>
        </div>
        {pai && (
          <div style={{ color: "#475569", fontSize: 12, marginBottom: 4 }}>
            Filho de: <span style={{ color: "#60a5fa", fontWeight: 700 }}>#{pai.id_projeto} — {pai.descricao}</span>
          </div>
        )}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <label style={fieldLabel}>
            Descrição *
            <input autoFocus value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Nome do projeto" style={fieldInput} />
          </label>
          <div style={{ display: "flex", gap: 12 }}>
            <label style={{ ...fieldLabel, flex: 1 }}>
              Importância
              <input type="number" value={importancia} onChange={e => setImportancia(e.target.value)} placeholder="ex: 500" style={fieldInput} />
            </label>
            <label style={{ ...fieldLabel, flex: 1 }}>
              Deadline
              <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} style={fieldInput} />
            </label>
          </div>
          {erro && <div style={erroMsg}>{erro}</div>}
          <div style={modalActions}>
            <button type="button" onClick={onClose} style={btnSec} disabled={saving}>Cancelar</button>
            <button type="submit" style={{ ...btnPri, opacity: saving ? 0.7 : 1 }} disabled={saving}>
              {saving ? "Criando..." : "Criar projeto"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmModal({ tipo, proj, onClose, onConfirm }) {
  const [saving, setSaving] = useState(false);
  const [erro,   setErro]   = useState(null);
  const isExcluir = tipo === "excluir";
  const color     = isExcluir ? "#f87171" : "#fbbf24";

  async function handleConfirm() {
    setSaving(true); setErro(null);
    try { await onConfirm(); }
    catch (err) { setErro(err?.response?.data?.error || "Erro ao executar ação."); setSaving(false); }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modalBox, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div style={modalHead}>
          <h2 style={{ ...modalTitle, color }}>{isExcluir ? "Excluir Projeto" : "Inativar Projeto"}</h2>
          <button onClick={onClose} style={closeBtn}>✕</button>
        </div>
        <p style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.6 }}>
          {isExcluir ? "Esta ação é irreversível. Confirma a exclusão:" : "O projeto será marcado como inativo:"}
        </p>
        <div style={{ background: `${color}10`, border: `1px solid ${color}30`, borderRadius: 10, padding: "10px 14px", color, fontWeight: 700, fontSize: 13 }}>
          #{proj.id_projeto} — {proj.descricao}
        </div>
        {erro && <div style={erroMsg}>{erro}</div>}
        <div style={modalActions}>
          <button onClick={onClose} style={btnSec} disabled={saving}>Cancelar</button>
          <button onClick={handleConfirm}
            style={{ ...btnPri, background: `${color}18`, borderColor: `${color}40`, color, opacity: saving ? 0.7 : 1 }}
            disabled={saving}>
            {saving ? "Aguarde..." : isExcluir ? "Confirmar exclusão" : "Confirmar inativação"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── CountdownRing ───────────────────────────────── */
function CountdownRing({ seconds, total }) {
  const r = 11, circ = 2 * Math.PI * r, filled = circ * (seconds / total);
  const color = seconds <= 15 ? "#f87171" : "#60a5fa";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <svg width={30} height={30} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
        <circle cx={15} cy={15} r={r} fill="none" stroke="rgba(96,165,250,0.1)" strokeWidth={2.5} />
        <circle cx={15} cy={15} r={r} fill="none" stroke={color} strokeWidth={2.5}
          strokeDasharray={circ} strokeDashoffset={circ - filled} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s linear, stroke 0.3s" }} />
      </svg>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
        <span style={{ color, fontSize: 14, fontWeight: 800, letterSpacing: -0.5 }}>
          {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
        </span>
        <span style={{ color: "#334155", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5 }}>próx. atualiz.</span>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div style={page}>
      <div style={{ height: 32, width: 260, borderRadius: 10, background: "rgba(255,255,255,0.04)", marginBottom: 16 }} />
      <div style={{ display: "flex", gap: 12, flex: 1 }}>
        <div style={{ width: 340, borderRadius: 14, background: "rgba(255,255,255,0.03)" }} />
        <div style={{ flex: 1,    borderRadius: 14, background: "rgba(255,255,255,0.02)" }} />
      </div>
    </div>
  );
}

/* ── Styles ───────────────────────────────────────── */
const glass = {
  background: "rgba(11,22,42,0.72)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)", borderRadius: 14,
  boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
};

const page      = { display: "flex", flexDirection: "column", gap: 12, height: "calc(100vh - 56px)", overflow: "hidden" };
const header    = { display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 };
const h1        = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", letterSpacing: -0.5 };
const badge     = { padding: "3px 12px", borderRadius: 20, background: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.25)", color: "#60a5fa", fontSize: 12, fontWeight: 700 };
const refreshBtn = { width: 34, height: 34, borderRadius: 8, border: "1px solid rgba(96,165,250,0.2)", background: "rgba(96,165,250,0.08)", color: "#60a5fa", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s, border-color 0.15s", flexShrink: 0, lineHeight: 1 };

const splitWrap  = { display: "flex", gap: 12, flex: 1, minHeight: 0, overflow: "hidden" };
const leftPanel  = { ...glass, width: 480, flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden" };
const rightPanel = { ...glass, flex: 1, overflow: "auto" };

const treeHeader = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", borderBottom: "1px solid rgba(96,165,250,0.08)", flexShrink: 0 };
const treeTitleStyle = { fontSize: 10, fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: 1 };
const newRootBtn = { padding: "3px 10px", borderRadius: 6, border: "1px solid rgba(74,222,128,0.3)", background: "rgba(74,222,128,0.08)", color: "#4ade80", fontSize: 11, fontWeight: 700, cursor: "pointer" };
const treeList   = { overflowY: "auto", flex: 1, padding: "6px 0" };

const treeItem = {
  display: "flex", alignItems: "center", gap: 6,
  padding: "7px 10px", cursor: "pointer",
  borderLeft: "3px solid transparent",
  transition: "background 0.12s",
  borderRadius: "0 8px 8px 0",
  marginRight: 6,
};
const treeItemLeft  = { display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 };
const treeItemRight = { display: "flex", alignItems: "center", gap: 6, flexShrink: 0 };
const treeActions   = { display: "flex", gap: 2 };
const treeBtn       = { width: 20, height: 20, borderRadius: 4, border: "none", background: "transparent", cursor: "pointer", fontSize: 11, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 };

const subBadge  = { background: "rgba(96,165,250,0.12)", color: "#60a5fa", fontSize: 9, fontWeight: 800, padding: "1px 5px", borderRadius: 8 };
const impPill    = { padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 700, border: "1px solid", whiteSpace: "nowrap" };
const statusPill = { padding: "1px 7px", borderRadius: 8, fontSize: 9, fontWeight: 700, border: "1px solid", whiteSpace: "nowrap" };

/* detail */
const detailWrap  = { padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 };
const detailHead  = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 };
const detailTitle = { color: "#f1f5f9", fontSize: 18, fontWeight: 800, letterSpacing: -0.3, margin: 0 };
const kpiRow      = { display: "flex", gap: 12 };
const section     = { display: "flex", flexDirection: "column", gap: 10 };
const sectionTitle = { fontSize: 10, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: 1 };
const nivelPill   = { padding: "2px 8px", borderRadius: 6, fontSize: 9, fontWeight: 700, background: "rgba(148,163,184,0.08)", border: "1px solid rgba(148,163,184,0.15)", color: "#475569" };
const clientePill = { padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.2)", color: "#a78bfa" };
const impBadgeLg  = { padding: "6px 14px", borderRadius: 10, fontSize: 12, fontWeight: 700, border: "1px solid", whiteSpace: "nowrap", flexShrink: 0 };
const emptyDetail = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "#334155" };

/* table */
const table = { width: "100%", borderCollapse: "collapse", fontSize: 12 };
const th    = { padding: "8px 12px", textAlign: "left", color: "#334155", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, borderBottom: "1px solid rgba(96,165,250,0.08)", whiteSpace: "nowrap", background: "rgba(0,0,0,0.12)" };
const td    = { padding: "8px 12px", borderBottom: "1px solid rgba(255,255,255,0.03)", color: "#94a3b8", verticalAlign: "middle" };
const chipSim = { padding: "1px 8px", borderRadius: 10, background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.25)", color: "#4ade80", fontSize: 10, fontWeight: 700 };
const chipNao = { padding: "1px 8px", borderRadius: 10, background: "rgba(148,163,184,0.08)", border: "1px solid rgba(148,163,184,0.15)", color: "#475569", fontSize: 10 };

/* modal */
const overlay     = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 };
const modalBox    = { background: "#0b1629", border: "1px solid rgba(96,165,250,0.2)", borderRadius: 18, padding: "26px 28px", width: 480, maxWidth: "92vw", boxShadow: "0 28px 72px rgba(0,0,0,0.8)", display: "flex", flexDirection: "column", gap: 14 };
const modalHead   = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const modalTitle  = { color: "#f1f5f9", fontSize: 16, fontWeight: 800, margin: 0 };
const closeBtn    = { background: "none", border: "none", color: "#475569", fontSize: 18, cursor: "pointer", lineHeight: 1 };
const modalActions = { display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 4 };
const fieldLabel  = { display: "flex", flexDirection: "column", gap: 5, fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5 };
const fieldInput  = { padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(96,165,250,0.2)", background: "rgba(255,255,255,0.04)", color: "#e2e8f0", fontSize: 13, outline: "none" };
const erroMsg     = { color: "#f87171", fontSize: 12, padding: "8px 12px", borderRadius: 8, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)" };
const btnSec      = { padding: "9px 20px", borderRadius: 9, border: "1px solid rgba(255,255,255,0.08)", background: "transparent", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const btnPri      = { padding: "9px 20px", borderRadius: 9, border: "1px solid rgba(96,165,250,0.4)", background: "rgba(96,165,250,0.15)", color: "#60a5fa", fontSize: 13, fontWeight: 700, cursor: "pointer" };
const errorWrap   = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 12 };
